import {animate, style, transition, trigger} from '@angular/animations';
import {
  AfterViewInit,
  Component,
  ElementRef, Inject,
  Input,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren
} from '@angular/core';
import {FormArray, FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {TranslocoService} from '@ngneat/transloco';
import {AbstractComponent} from "@components";
import {
  EventItem,
  FieldConfig, FieldConfigOption,
  FieldConfigType,
  Lookups,
  Race,
  RegistrationDisplayFlags,
  VisibilityTypes
} from "@model";
import {required} from "@utility";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {DOCUMENT} from "@angular/common";

@Component({
  selector: 'app-race-details-registration-settings-custom-field-form',
  templateUrl: './race-details-registration-settings-custom-field-form.component.html',
  styleUrls: ['./race-details-registration-settings-custom-field-form.component.scss'],
  animations: [
    trigger('itemAnimation', [
      transition(':enter', [
        style({opacity: 0,}),
        animate('300ms', style({opacity: 1,}))
      ]),
      transition(':leave', [
        animate('100ms', style({opacity: 0,}))
      ])
    ])]
})
export class RaceDetailsRegistrationSettingsCustomFieldFormComponent extends AbstractComponent implements OnInit, AfterViewInit, OnChanges {

  @ViewChildren('options') optionsQuery?: QueryList<ElementRef>;

  @Input() customField: FieldConfig = new FieldConfig();
  @Input() race: Race | undefined;
  @Input() event: EventItem | undefined;
  @Input() visibilityTypes: Lookups[] = [];
  @Input() registrationDisplayFlags: Lookups[] = [];
  @Input() fieldConfigTypes: Lookups[] = [];
  @Input() publicVisibilitySelectionDisabled: boolean = true;

  inputForm = new FormGroup({
    id: new FormControl(),
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    nameSr: new FormControl(),
    nameBs: new FormControl(),
    info: new FormControl(),
    infoEng: new FormControl(),
    infoIta: new FormControl(),
    infoSlo: new FormControl(),
    infoSr: new FormControl(),
    infoBs: new FormControl(),
    fieldConfigTypeId: new FormControl(),
    registrationDisplayFlag: new FormControl(),
    required: new FormControl(),
    visibility: new FormControl(),
    options: this.formBuilder.array<FormGroup>([]),
    minValue: new FormControl(),
    maxValue: new FormControl(),
    minTextLength: new FormControl(),
    maxTextLength: new FormControl(),
  });

  constructor(
    public translocoService: TranslocoService, public formBuilder: FormBuilder, public activeModal: NgbActiveModal,
    @Inject(DOCUMENT) private document: Document) {
    super();
  }

  ngOnInit() {
    if (this.publicVisibilitySelectionDisabled) {
      this.visibilityTypes = this.visibilityTypes.filter(item=>item.id == VisibilityTypes.NOT_VISIBLE || item.id == VisibilityTypes.VISIBLE_TO_ADMINISTRATION);
    }
    this.subscribeToFieldConfigTypeValueChange();
    this.subscribeToFormStatusChange();
    this.subscribeToRegistrationDisplayFlagChange();
    this.initForm();
  }

  ngAfterViewInit() {
    if (this.optionsQuery) {
      this.subscriptions.push(
        this.optionsQuery.changes.subscribe(_ => {
          this.document.getElementById("optionsHolder")?.scrollTo({
            top: this.optionsQuery?.last?.nativeElement?.offsetTop ?? 0,
            behavior: 'smooth'
          });
        })
      )
    }
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customField']?.currentValue !== changes['customField']?.previousValue) {
      this.initForm();
    }
  }

  private subscribeToRegistrationDisplayFlagChange() {
    this.subscriptions.push(this.inputForm!.controls.registrationDisplayFlag.valueChanges.subscribe(value => {
      if (!value || value === RegistrationDisplayFlags.NOT_USED) {
        this.inputForm?.controls.visibility.setValue(VisibilityTypes.NOT_VISIBLE)
        this.inputForm?.controls.visibility.disable()
        this.inputForm?.controls.required.setValue(false);
        this.inputForm?.controls.required.disable()
      } else {
        this.inputForm?.controls.visibility.enable();
        this.inputForm?.controls.required.setValue(true);
        this.inputForm?.controls.required.enable();
      }
    }))
  }

  private subscribeToFormStatusChange() {
    this.subscriptions.push(this.inputForm!.controls.options.statusChanges.subscribe(_ => {
      this.inputForm.updateValueAndValidity();
    }))
  }

  get options(): FormArray {
    return this.inputForm?.controls['options'] as FormArray;
  }

  optionFormControl(i: number): FormGroup {
    return (this.inputForm.controls['options']?.controls.at(i)) as FormGroup;
  }

  public get fieldConfigType(): typeof FieldConfigType {
    return FieldConfigType;
  }

  private subscribeToFieldConfigTypeValueChange() {
    if (this.inputForm) {
      this.subscriptions.push(this.inputForm.controls.fieldConfigTypeId.valueChanges.subscribe((value) => {
        this.inputForm.controls.minValue.setValue(undefined);
        this.inputForm.controls.minValue.removeValidators([required]);
        this.inputForm.controls.minValue.updateValueAndValidity();
        this.inputForm.controls.minValue.markAsUntouched();
        this.inputForm.controls.maxValue.setValue(undefined);
        this.inputForm.controls.maxValue.removeValidators([required]);
        this.inputForm.controls.maxValue.updateValueAndValidity();
        this.inputForm.controls.maxValue.markAsUntouched();
        this.inputForm.controls.minTextLength.setValue(undefined);
        this.inputForm.controls.minTextLength.removeValidators([required]);
        this.inputForm.controls.minTextLength.updateValueAndValidity();
        this.inputForm.controls.minTextLength.markAsUntouched();
        this.inputForm.controls.maxTextLength.setValue(undefined);
        this.inputForm.controls.maxTextLength.removeValidators([required]);
        this.inputForm.controls.maxTextLength.updateValueAndValidity();
        this.inputForm.controls.maxTextLength.markAsUntouched();
        this.inputForm.controls.options = this.formBuilder.array<FormGroup>([]);
        this.inputForm.controls.options.removeValidators([required]);

        switch (value) {
          case FieldConfigType.TEXT:
            this.inputForm.controls.minTextLength.setValidators([required]);
            this.inputForm.controls.maxTextLength.setValidators([required]);
            this.inputForm.controls.minTextLength.updateValueAndValidity();
            this.inputForm.controls.maxTextLength.updateValueAndValidity();
            break;
          case FieldConfigType.NUMBER:
            this.inputForm.controls.maxValue.setValidators([required]);
            this.inputForm.controls.minValue.setValidators([required]);
            this.inputForm.controls.maxValue.updateValueAndValidity();
            this.inputForm.controls.minValue.updateValueAndValidity();

            break;
          case FieldConfigType.MULTISELECT:
          case FieldConfigType.SELECT:
            this.inputForm.controls.options.setValidators([required]);
            this.inputForm.controls.options.updateValueAndValidity();
            if(!((this.customField?.options?.length ?? 0) > 0)){
              this.onAddOption();
            }
            break;
          default:
            break;
        }
        this.inputForm.updateValueAndValidity();
      }));
    }

  }

  private initForm() {
    if (this.inputForm) {
      this.inputForm.reset();
      this.inputForm.patchValue(this.customField);
      this.inputForm.controls.options = this.formBuilder.array<FormGroup>([]);
      this.inputForm.controls.required.setValue(this.customField?.required ?? false);
      this.inputForm.controls.registrationDisplayFlag.setValue(this.customField?.registrationDisplayFlag ?? RegistrationDisplayFlags.NOT_USED);
      this.inputForm.controls.visibility.setValue(this.customField?.visibility ?? VisibilityTypes.NOT_VISIBLE);
      this.inputForm.controls.fieldConfigTypeId.setValue(this.customField?.fieldConfigTypeId ?? FieldConfigType.BOOLEAN);
      this.inputForm.controls.required.setValue(this.customField?.required ?? false);
      if (this.customField?.options) {
        this.customField?.options.forEach((option: FieldConfigOption) => {
          let formControl = this.createOption(option);
          this.options.push(formControl);
        })
      }
      if (this.customField.id) {
        this.inputForm.controls.fieldConfigTypeId.disable({emitEvent:false});
      }
      else {
        this.inputForm.controls.fieldConfigTypeId.enable({emitEvent:false});
      }
    }
  }

  removeFieldConfigOption(i: number) {
    this.options.removeAt(i);
    if (this.options.length === 0) {
      this.onAddOption();
    }
  }

  private createOption(option?: FieldConfigOption): FormGroup {
    return this.formBuilder.group({
      id: [option?.id],
      name: [option?.name ?? ''],
      nameEng: [option?.nameEng ?? ''],
      nameIta: [option?.nameIta ?? ''],
      nameSlo: [option?.nameSlo ?? ''],
      nameSr: [option?.nameSr ?? ''],
      nameBs: [option?.nameBs ?? '']
    });
  }


  onAddOption() {
    let formControl = this.createOption();
    this.options.push(formControl);
  }

  getFieldConfig(): FieldConfig {
    let customField = {...this.customField};
    customField.raceId = this.race?.id;
    customField.name = this.inputForm?.controls.name.value;
    customField.nameEng = this.inputForm?.controls.nameEng.value;
    customField.nameIta = this.inputForm?.controls.nameIta.value;
    customField.nameSlo = this.inputForm?.controls.nameSlo.value;
    customField.nameSr = this.inputForm?.controls.nameSr.value;
    customField.nameBs = this.inputForm?.controls.nameBs.value;
    customField.info = this.inputForm?.controls.info.value;
    customField.infoEng = this.inputForm?.controls.infoEng.value;
    customField.infoIta = this.inputForm?.controls.infoIta.value;
    customField.infoSlo = this.inputForm?.controls.infoSlo.value;
    customField.infoSr = this.inputForm?.controls.infoSr.value;
    customField.infoBs = this.inputForm?.controls.infoBs.value;
    customField.fieldConfigTypeId = this.inputForm?.controls.fieldConfigTypeId.value ?? undefined;
    customField.registrationDisplayFlag = this.inputForm?.controls.registrationDisplayFlag.value ?? undefined;
    customField.required = this.inputForm?.controls.required.value ?? undefined;
    customField.visibility = this.inputForm?.controls.visibility.value ?? undefined;
    customField.minValue = this.inputForm?.controls.minValue.value;
    customField.maxValue = this.inputForm?.controls.maxValue.value;
    customField.minTextLength = this.inputForm?.controls.minTextLength.value;
    customField.maxTextLength = this.inputForm?.controls.maxTextLength.value;
    let options: FieldConfigOption[] = [];
    if (this.options.length > 0) {
      this.options.controls.forEach((element) => {
        let option = new FieldConfigOption();
        option.id = element.getRawValue().id;
        option.name = element.getRawValue().name
        option.nameSlo = element.getRawValue().nameSlo
        option.nameSr = element.getRawValue().nameSr
        option.nameBs = element.getRawValue().nameBs
        option.nameIta = element.getRawValue().nameIta
        option.nameEng = element.getRawValue().nameEng
        option.fieldConfigId = this.customField?.id;
        option.raceId = this.race?.id;
        options.push(option);
      });
      customField.options = options;
    }
    return customField;
  }

  isValid(): boolean {
    if (this.inputForm.valid) {
      if (this.inputForm?.controls.fieldConfigTypeId.value === FieldConfigType.MULTISELECT || this.inputForm.controls.fieldConfigTypeId.value === FieldConfigType.SELECT) {
        return (this.inputForm!.controls.options.valid && this.options.length > 0)
      }
      return true;
    } else return false;
  }

  onSave() {
    if (this.isValid()) {
      this.activeModal.close(this.getFieldConfig());
    } else {
      this.markAllAsTouched(this.inputForm)
    }
  }

  onCancel() {
    this.activeModal.dismiss('cancel');
  }

  onDelete() {
    this.activeModal.dismiss('delete');
  }

  protected readonly FieldConfigType = FieldConfigType;
}
